import { createSlice } from '@reduxjs/toolkit';

import type { GenericError, JsonFailure, RequestStatus } from '@skynet/data-ws-core';
import { registerErrorMessage, isOkCommand, parseResponse } from '@skynet/data-ws-core';
import { actionOnMessage } from '@skynet/data-ws-mm';

import type { BindUnbindAccountResult, JsonBindAccountPayload } from './types';
import { AdminPanelCommands } from './types';
import { bindAccount, unbindAccount } from 'src/store/retainers/admin-panel/actions';

// Bind and unbind both travel over the `BindAccount` WS command, so a single
// registration covers failures of either action. Without it, `onError` returns
// null and the request status would stay stuck on 'pending' after a failure.
registerErrorMessage(AdminPanelCommands.BindAccount, {
  title: 'Failed to update member list',
  getDescription: (json: JsonFailure) => String(json.message),
});

registerErrorMessage(AdminPanelCommands.AdminPanel, {
  title: 'Admin Panel Error',
  getDescription: (json: JsonFailure) => String(json.message),
});

export interface JsonProjectAdmin {
  id: number;
  name: string;
  // accounts: number[];
  users: number[];
}

export interface JsonUserAccount {
  id: number;
  name: string;
}

export interface RetainersAdminPanelState {
  requestStatus: RequestStatus;
  error: null | GenericError;
  resBindAccount: RequestStatus;
  resUnbindAccount: RequestStatus;
  resBindAccountErrorMessage: string | null;
  projects: JsonProjectAdmin[] | null;
  userAccounts: JsonUserAccount[];
}

const allowedCommands = Object.values(AdminPanelCommands);

export const isBindUnbindAccountResult = (value: unknown): value is BindUnbindAccountResult => {
  if (!value || typeof value !== 'object') return false;
  if (!('accountID' in value) || !('projectID' in value) || !('direction' in value)) return false;
  const v = value as { [key: string]: unknown };
  return (
    typeof v.accountID === 'number' &&
    typeof v.projectID === 'number' &&
    typeof v.direction === 'number'
  );
};

const parseBindAccountResult = (jsonString: string): BindUnbindAccountResult | null => {
  try {
    const parsed: unknown = JSON.parse(jsonString);
    if (isBindUnbindAccountResult(parsed)) return parsed;
    return null;
  } catch {
    return null;
  }
};

export const initialState: RetainersAdminPanelState = {
  requestStatus: null,
  error: null,
  resBindAccount: null,
  resUnbindAccount: null,
  resBindAccountErrorMessage: null,
  projects: null,
  userAccounts: [],
};

const retainersAdminPanelSlice = createSlice({
  name: 'adminPanel', // retainers.adminPanel
  initialState,
  reducers: {
    /**
     * Clears the transient bind/unbind request statuses and the cached error
     * message. Consumers dispatch this once they have reacted to a terminal
     * result — success or error — so a lingering status cannot re-trigger the
     * reaction on the next status change, and `isLoading` can recover after a
     * failed request.
     */
    resetBindUnbindStatus: (state) => {
      state.resBindAccount = null;
      state.resUnbindAccount = null;
      state.resBindAccountErrorMessage = null;
    },
  },
  extraReducers: (builder) => {
    // on WS message

    builder.addCase(bindAccount, (state) => {
      state.resBindAccount = 'pending';
      state.resBindAccountErrorMessage = null;
    });

    builder.addCase(unbindAccount, (state) => {
      state.resUnbindAccount = 'pending';
    });

    builder.addCase(actionOnMessage, (state, action) => {
      const { json, skipProcessing, error, errorCommand } = parseResponse<JsonBindAccountPayload>(
        action.payload,
        allowedCommands,
      );

      if (skipProcessing || !json) return;

      console.debug(
        `retainers/adminPanel/processing ${actionOnMessage.toString()}`,
        action,
        allowedCommands,
      );

      const { command, okCommand, message } = json;

      if (error) {
        state.error = error;

        switch (errorCommand) {
          case AdminPanelCommands.AdminPanel:
            state.requestStatus = 'error';
            break;
          case AdminPanelCommands.BindAccount:
            // Bind and unbind share the same WS command, so the error response
            // cannot tell them apart. Mark whichever request is in flight as
            // failed so `isLoading` recovers instead of hanging on 'pending'.
            state.resBindAccountErrorMessage = message;
            if (state.resBindAccount === 'pending') {
              state.resBindAccount = 'error';
            }
            if (state.resUnbindAccount === 'pending') {
              state.resUnbindAccount = 'error';
            }
            break;
          case undefined:
          default:
        }
      }

      if (command === AdminPanelCommands.AdminPanel) {
        state.userAccounts = json.accounts;
        state.projects = json.projects;
        state.requestStatus = 'success';
        return;
      }
      if (isOkCommand(command) && okCommand === AdminPanelCommands.BindAccount) {
        state.error = null;
        const result = parseBindAccountResult(json.result);

        if (result) {
          const { accountID, projectID, direction } = result;
          const project = state.projects?.find((p) => p.id === projectID);
          if (project) {
            if (direction === 1) {
              project.users.push(accountID);
              state.resBindAccount = 'success';
            } else if (direction === 0) {
              project.users = project.users.filter((id) => id !== accountID);
              state.resUnbindAccount = 'success';
            }
          }
        } else {
          state.resBindAccount = 'error';
          state.resBindAccountErrorMessage = message;
        }
      }

      // if (command === 'ok') {
      //   switch (okCommand) {
      //     // case 'Projects':
      //     //   state.list = json.list;
      //     //   break;
      //
      //     default:
      //   }
      // }
    });
  },
});

export const retainersProjectsActions = { ...retainersAdminPanelSlice.actions };
export const { resetBindUnbindStatus } = retainersAdminPanelSlice.actions;
// export const { toggleSidebar } = retainersAdminPanelSlice.actions;

export default retainersAdminPanelSlice.reducer;
